import { ShopifyArticle } from 'lib/shopify/blog';
import { ArrowUpRight } from 'lucide-react';
import Link from 'next/link';

interface BlogCardProps {
  article: ShopifyArticle;
  blogHandle?: string;
}

function slugifyTag(tag: string) {
  return tag.toLowerCase().replace(/\s+/g, '-');
}

export function BlogCard({ article, blogHandle }: BlogCardProps) {
  // Use the provided blogHandle or get it from the article's blog relationship
  const activeBlogHandle = blogHandle || article.blog?.handle || 'blog';

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white transition-shadow">
      {/* Image container with responsive height */}
      <div
        className="relative h-32 w-full bg-cover bg-center sm:h-40 lg:h-48"
        style={{
          backgroundImage: article.image ? `url(${article.image.url})` : 'none',
          backgroundColor: article.image ? 'transparent' : '#f3f4f6'
        }}
      />
      
      {/* Content container with flex-1 to fill remaining space */}
      <div className="flex flex-1 flex-col justify-start py-4 sm:py-6">
        <div className="flex-1">
          {/* Date */}
          <div className="text-xs sm:text-sm text-gray-500">
            {new Date(article.publishedAt).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            })}
          </div>
          
          {/* Title with consistent height - using fixed height approach */}
          <Link href={`/about/blogs/${activeBlogHandle}/${article.handle}`} className="group">
            <h3 className="mt-2 flex items-start justify-between text-sm font-bold text-gray-900 group-hover:text-gray-600 sm:text-base lg:text-lg leading-tight">
              <span 
                className="flex-1 pr-2 overflow-hidden"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.25',
                  maxHeight: '2.5em'
                }}
              >
                {article.title}
              </span>
              <span className="mt-0.5 inline-block flex-shrink-0">
                <ArrowUpRight size={16} className="text-gray-900 sm:size-5 lg:size-6" />
              </span>
            </h3>
          </Link>
          
          {/* Description with consistent height - using fixed height approach */}
          <p 
            className="mt-2 text-xs sm:text-sm text-gray-500 leading-relaxed overflow-hidden"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: '1.4',
              maxHeight: '2.8em'
            }}
          >
            {article.excerpt || article.content.substring(0, 80)}
          </p>
        </div>
        
        {/* Tags - fixed at bottom */}
        <div className="mt-4 flex flex-wrap gap-1 sm:gap-2">
          {(article.tags || []).slice(0, 2).map((tag) => (
            <Link
              key={tag}
              href={`/about/blogs/tag?tag=${encodeURIComponent(slugifyTag(tag))}`}
              className="rounded-sm bg-gray-100 px-2 py-1 text-xs font-normal text-gray-500 transition-colors hover:bg-gray-200 sm:text-sm"
            >
              {tag}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
